import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadsAdmin = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/leads`);
      if (response.data.success) {
        setLeads(response.data.data);
      }
      setError(null);
    } catch (error) {
      console.error("Error fetching leads:", error);
      setError("Failed to fetch leads. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",  
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getProjectTypeColor = (type) => {
    const colors = {
      "ai-app": "bg-blue-100 text-blue-800",
      "ml-solution": "bg-emerald-100 text-emerald-800", 
      "consulting": "bg-purple-100 text-purple-800",
      "agents": "bg-orange-100 text-orange-800",
      "architecture": "bg-indigo-100 text-indigo-800",
      "web-development": "bg-green-100 text-green-800",
      "mobile-development": "bg-cyan-100 text-cyan-800",
      "cloud-infrastructure": "bg-gray-100 text-gray-800",
      "other": "bg-slate-100 text-slate-800"
    };
    return colors[type] || colors["other"];
  };

  const getProjectTypeLabel = (type) => {
    const labels = {
      "ai-app": "AI App Development",
      "ml-solution": "Machine Learning Solution",
      "consulting": "AI Consulting", 
      "agents": "Agents & Multi-Agent Systems",
      "architecture": "AI Architecture Design",
      "web-development": "Web Development",
      "mobile-development": "Mobile Development", 
      "cloud-infrastructure": "Cloud Infrastructure",
      "other": "Other"
    };
    return labels[type] || type;
  };

  const exportToCSV = () => {
    const headers = ["Name", "Email", "Company", "Project Type", "Submitted Date", "Message"];
    const csvContent = [
      headers.join(","),
      ...filteredLeads.map(lead => [
        `"${lead.name}"`,
        `"${lead.email}"`, 
        `"${lead.company || 'N/A'}"`,
        `"${getProjectTypeLabel(lead.projectType)}"`,
        `"${formatDate(lead.createdAt)}"`,
        `"${lead.message.replace(/"/g, '""')}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `grassurn-leads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter and search functionality
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.company && lead.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         lead.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === "all" || lead.projectType === filterType;
    
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600 font-light">Loading leads...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "64px 0" }}>
        <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 13, color: "#DC2626", marginBottom: 16 }}>
          {error}
        </p>
        <button
          onClick={fetchLeads}
          style={{
            padding: "9px 24px",
            background: "#0A0A0A",
            border: "none",
            borderRadius: 6,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 500,
            fontSize: 13,
            color: "#fff",
            cursor: "pointer"
          }}
        >
          Retry
        </button>
        <p style={{ marginTop: 12, fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 11, color: "#9CA3AF" }}>
          Make sure the backend server is running on port 8000.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 14, color: "#0A0A0A", marginBottom: 4 }}>
          Leads
        </h1>
        <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#9CA3AF" }}>
          Website enquiries — read only, newest first
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px 8px 32px",
              border: "1px solid #E5E7EB",
              borderRadius: 6,
              background: "#fff",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 300,
              fontSize: 13,
              color: "#374151",
              outline: "none",
              width: 220
            }}
          />
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: 14 }}>⌕</span>
        </div>
        <button
          onClick={exportToCSV}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            background: "#2D5C5A",
            border: "none",
            borderRadius: 6,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 500,
            fontSize: 13,
            color: "#fff",
            cursor: "pointer"
          }}
        >
          Export CSV
        </button>
      </div>

      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F9FAFB" }}>
              {["Name", "Email", "Company", "Type", "Date", "Message"].map(col => (
                <th key={col} style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map((lead) => (
              <tr key={lead.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{lead.name}</td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{lead.email}</td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{lead.company || "—"}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11,
                    fontFamily: "Montserrat, sans-serif", fontWeight: 500,
                    background: "#F3F4F6", color: "#6B7280"
                  }}>
                    {getProjectTypeLabel(lead.projectType)}
                  </span>
                </td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#6B7280", whiteSpace: "nowrap" }}>
                  {formatDate(lead.createdAt)}
                </td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151", maxWidth: 200 }}>
                  <span style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {lead.message}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadsAdmin;